/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './Tableau';

const TableauManager: ComponentManagerFn<'Tableau'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Tableau'>(platform, 'Tableau'),
    type: 'Tableau',
    name: 'Tableau',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      url: {
        label: 'Report URL',
        type: 'string',
        required: true,
        helpText: 'Viz URL from the Share Options, not browser URL',
      },
      reportHeight: {
        label: 'Height',
        type: 'string',
        category: 'Size',
        helpText:
          "Any value between 0 & 1 are calculated relative to the parent container's height. e.g. a value of 1 is considered as the parent's height.",
        placeholder: 'e.g 1 or 200',
      },
      reportWidth: {
        label: 'Width',
        type: 'string',
        category: 'Size',
        helpText:
          "Any value between 0 & 1 are calculated relative to the parent container's width. e.g. a value of 1 is considered as the parent's width.",
        placeholder: 'e.g. 1 or 200',
      },
      hideToolBar: {
        label: 'Hide Tool Bar',
        type: 'boolean',
        category: 'General',
      },
      hideEditButton: {
        label: 'Hide Edit Button',
        type: 'boolean',
        category: 'General',
      },
      hideTab: {
        label: 'Hide Tabs',
        type: 'boolean',
        category: 'General',
      },
      toolBarPosition: {
        label: 'Tool Bar Position',
        type: 'select',
        options: { Top: 'Top', Bottom: 'Bottom' },
        category: 'General',
      },
    },
    getDisplayText: (item) => '',
    category: 'Forms',
    icon: { iconName: 'analytics' },
    description: `**Overview**<br/>
The Tableau component allows you to securely embed Tableau visualizations (Dashboards, Stories, or Worksheets) directly into your application.
Instead of generic IFrame embedding, this component uses the Tableau Javascript API to ensure seamless integration. It provides native control over the visualization's appearance, allowing you to hide toolbars, manage tabs, and control layout without writing custom integration code.

**1. Source Configuration**<br/>
To render a dashboard, you must point the component to the specific visualization on your Tableau Server or Tableau Cloud.
* **Report URL:** The specific "Viz URL" for the dashboard.
* **Important:** Do not copy the URL from the browser address bar. You must use the URL found in the "Share" options menu within the Tableau interface. (Example: \`https://public.tableau.com/views/Regional/GlobalTemperatures\`).

**2. Sizing Strategy**<br/>
The component offers a flexible sizing model that supports both fixed and responsive layouts.
* **Height / Width:** Accepts a numeric string value.
    * **Fixed Pixels:** If the value is greater than 1, it is interpreted as pixels (e.g., 800 = 800px).
    * **Relative Percentage:** If the value is between 0 and 1, it is interpreted as a percentage of the parent container (e.g., 1 = 100% width, 0.5 = 50% width).
    * **Recommendation:** Use relative values (like 1) for responsive dashboards that need to resize with the user's browser window.

**3. Display Customization**<br/>
You can sanitize the Tableau interface to make it look like a native part of your application by removing external branding and controls.
* **Hide Tool Bar:** Removes the standard Tableau bottom/top toolbar (Download, Revert, Undo). (Note: Enabling this hides the "Tool Bar Position" and "Hide Edit Button" settings as they become irrelevant).
* **Hide Tabs:** If the workbook contains multiple sheets/tabs, enabling this hides the tab selector at the top/bottom, forcing the user to view only the specific dashboard you linked.
* **Tool Bar Position:** If the toolbar is visible, you can dock it to the Top or Bottom of the visualization.
* **Hide Edit Button:** Removes the "Edit" button, preventing users from modifying the report even if they have permissions on the server.

**4. Interactivity**<br/>
* **TriggerOnClick:** You can add this child component to execute application logic when a user clicks on the visualization wrapper. This is useful for tracking engagement or logging when a user views a specific report.`,
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'toolBarPosition':
        case 'hideEditButton':
          return props.hideToolBar === false;
        default:
          return true;
      }
    },
  };
};

export default TableauManager;
